import { ThemeComponent } from '@/theme/ThemeComponent';
import { OptionSelector } from '@/utils/OptionSelectors';
import { Component, Prop, Ref, SimpleEvent } from '@eastsideco/salvo-ts';
import { ProductOption } from '@/components/Product/ProductOptionPicker';
import debounce from 'lodash/debounce';

interface ValueRadio extends HTMLInputElement {
    dataset: {
        value: string;
    };
}

@Component
export class ProductButtonOption extends ThemeComponent implements ProductOption {
    @Prop inStock!: string;
    @Prop name!: string;
    @Prop selected!: string;
    @Prop position!: number;
    @Prop strings!: string;

    @Ref radioWrapper!: HTMLElement[];
    @Ref({ required: false }) labelSelectedOption?: HTMLElement;
    @Ref({ required: false }) buttonSelectedOption?: HTMLElement;

    @Ref({ required: false }) optionDrawerTrigger !: HTMLButtonElement;
    @Ref({ required: false }) optionDrawer !: HTMLElement;
    @Ref({ required: false }) optionDrawerClose !: HTMLButtonElement;

    @Ref({ required: false }) inStockSelected!: HTMLElement[];

    private valuesWithoutStock: string[] = [];
    private valuesWithoutVariant: string[] = [];

    private _onChange = new SimpleEvent<string | null>();
    public get onChange() {
        return this._onChange.asEvent();
    }

    init() {
        const body = document.querySelector('body') as HTMLElement;

        for (const option of this.radioWrapper) {
            const button = option.querySelector('input') as ValueRadio;
            this.$listen(button, 'change', () => {
                if (!button.checked) { return; }

                const value = button.value;
                const stickyBarSize = document.querySelector('.selected-size');
                this.select(value);

                // Update label selected value
                if (this.labelSelectedOption && value) {
                    this.labelSelectedOption.textContent = value.toLowerCase();
                    if (stickyBarSize) {
                        stickyBarSize.textContent = value.toLowerCase();
                    }
                }
                if (this.buttonSelectedOption && value) {
                    this.buttonSelectedOption.textContent = `: ${value.toLowerCase()}`;
                }

                setTimeout(() => {
                    for (const stockSelected of this.inStockSelected) {
                        if (parseInt(this.inStock, 10) > 0) {

                            stockSelected.classList.remove('is-disabled');

                            if (parseInt(this.inStock, 10) < 5) {
                                stockSelected.innerText = this.getString('lessStock');
                            } else {
                                stockSelected.innerText = this.getString('inStock');
                            }
                        } else {
                            stockSelected.innerText = this.getString('outOfStock');
                            stockSelected.classList.add('is-disabled');
                        }
                    }

                }, 300);

                // Close drawer
                document.dispatchEvent(new Event('closeSizeDrawer'));
                this.optionDrawer?.classList.remove('is-active');
                this.optionDrawerTrigger?.classList.remove('is-active');

                this._onChange.dispatch(value);
            });
        }

        if (this.optionDrawerTrigger) {
            this.$listen(this.optionDrawerTrigger, 'click', () => {
                this.optionDrawerTrigger.classList.add('is-active');
                this.optionDrawer.classList.add('is-active', 'is-drawer');
                body.classList.add('options-open');
            });

            document.addEventListener('openSizeDrawer', () => {
                this.optionDrawerTrigger.classList.add('is-active');
                this.optionDrawer.classList.add('is-active', 'is-drawer');
                body.classList.add('options-open');
            });
        }

        document.addEventListener('openSizeDrawer', () => {
            if ((!this.optionDrawer.classList.contains('is-short') ||
				window.matchMedia('(max-width: 949px)').matches) && !this.classList.contains('is-quick-add')) {
                this.optionDrawer.classList.add('is-active', 'is-drawer');
                body.classList.add('options-open');
            }
        });

        if (this.optionDrawerClose) {
            this.$listen(this.optionDrawerClose, 'click', () => {
                if (this.optionDrawer.classList.contains('is-short')) {
                    this.optionDrawer.classList.remove('is-active', 'is-drawer');
                } else {
                    this.optionDrawer.classList.remove('is-active');
                }
                body.classList.remove('options-open');
                document.dispatchEvent(new Event('closeSizeDrawer'));
            });
        }
        // this._onChange.dispatch(this.selected);

        document.addEventListener('closeSizeDrawer', () => {
            this.optionDrawerTrigger?.classList.remove('is-active');
            if (this.optionDrawer.classList.contains('is-short')) {
                this.optionDrawer?.classList.remove('is-active', 'is-drawer');
            } else {
                this.optionDrawer?.classList.remove('is-active');
            }
            body.classList.remove('options-open');
        });

        this.listenForOuterDrawerClick();
    }

    public listenForOuterDrawerClick() {
        this.$listen(this.optionDrawer, 'click', (e) => {
            if (e.target) {
                const clickedElement = e.target as HTMLElement;
                if (clickedElement === this.optionDrawer) {
                    this.optionDrawer.classList.remove('is-active');
                    document.body.classList.remove('options-open');
                }
            }
        });
    }

    public select(value: string) {
        this.selected = value;
        this.updateButtonStyles();
    }

    public updateBasedOnSelector(selector: OptionSelector) {
        this.valuesWithoutStock = selector.options[this.position - 1].values
            .filter((v) => v.outOfStock).map((v) => v.value);
        this.valuesWithoutVariant = selector.options[this.position - 1].values
            .filter((v) => !v.variantExists).map((v) => v.value);

        setTimeout(() => {
            this.updateButtonStyles();
        }, 300);
    }

    private updateButtonStyles() {
        for (const option of this.radioWrapper) {
            const button = option.querySelector('input') as ValueRadio;
            const value = button.value;
            const selected = (value || null) == this.selected;
            const outOfStock = this.valuesWithoutStock.indexOf(value) > -1;
            const noVariant = this.valuesWithoutVariant.indexOf(value) > -1;

            if (button.parentNode == null) { return; }

            const wrapper = button.parentNode as HTMLElement;

            wrapper.classList.remove(
                'product-button-option__button-item--active',
                'product-button-option__button-item--no-variant',
            );

            if (noVariant) {
                wrapper.classList.add('product-button-option__button-item--no-variant');
                return;
            }

            if (selected) {
                wrapper.classList.add('product-button-option__button-item--active');
            }

            if (outOfStock) {
                wrapper.classList.add('product-button-option__button-item--out-of-stock');
            }
        }
    }

    protected getString(string: string) {
        const strings = JSON.parse(this.strings);
        return strings[string];
    }
}
